import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  DAYS_IN_MONTH,
  DAYS_IN_MONTH_BY_MONTH,
  DAYS_OF_WEEK,
  DAYS_OF_WEEK_SHORT_OPTIONS,
  DATE_FORMATS_OPTIONS,
  DATE_PATTERNS,
  DATE_FORMATS,
  MONTHS_IN_YEAR,
  START_OF_WEEK_OPTIONS,
  TIME_FORMATS_OPTIONS,
  TIME_PATTERNS,
  TIME_FORMATS,
} from 'constants/date';
import {
  REMINDER_TYPES,
  REMINDER_FREQUENCIES,
} from 'constants/reminders';

export function formatDateToLocal(dateString, formatString = 'MM-dd-yyyy HH:mm') {
  if (!dateString) return '';
  return format(parseISO(dateString), formatString);
}

export function getDateTimePatterns(settings = {}) {
  const dateFormat = settings?.dateFormat || DATE_FORMATS.MM_DD_YYYY;
  const timeFormat = settings?.timeFormat || TIME_FORMATS.H24;

  const datePattern = DATE_PATTERNS[dateFormat] || DATE_PATTERNS[DATE_FORMATS.MM_DD_YYYY];
  const timePattern = timeFormat === TIME_FORMATS.H12 ? TIME_PATTERNS.H12 : TIME_PATTERNS.H24;

  return {
    datePattern,
    timePattern,
    dateTimePattern: `${datePattern} ${timePattern}`,
  };
}

export function getMaxDayForMonth(month) {
  if (!Number.isInteger(month) || month < 1 || month > MONTHS_IN_YEAR) {
    return DAYS_IN_MONTH;
  }

  return DAYS_IN_MONTH_BY_MONTH[month];
}

export function isValidMonthDay(month, day) {
  if (!Number.isInteger(month) || month < 1 || month > MONTHS_IN_YEAR) {
    return false;
  }

  if (!Number.isInteger(day) || day < 1) {
    return false;
  }

  return day <= getMaxDayForMonth(month);
}

export function formatMonthDay(month, day, dateFormat = DATE_FORMATS.MM_DD_YYYY) {
  if (!isValidMonthDay(month, day)) {
    return '';
  }

  const monthLabel = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2024, month - 1, day)));

  if (dateFormat === DATE_FORMATS.DD_MM_YYYY) {
    return `${day} ${monthLabel}`;
  }

  return `${monthLabel} ${day}`;
}

export function getRegionalClockDisplay(localePreferences = {}, now = new Date()) {
  const {
    timezone,
    locale,
    dateFormat = DATE_FORMATS.MM_DD_YYYY,
    timeFormat = TIME_FORMATS.H24,
    startOfWeek = DAYS_OF_WEEK.SUNDAY,
  } = localePreferences;

  const browserDefaults = Intl.DateTimeFormat().resolvedOptions();
  const resolvedLocale = locale || browserDefaults.locale;

  let timeZone = browserDefaults.timeZone;
  if (timezone?.trim()) {
    try {
      Intl.DateTimeFormat(resolvedLocale, { timeZone: timezone.trim() }).format(now);
      timeZone = timezone.trim();
    } catch {
      // invalid IANA id — keep browser fallback
    }
  }

  const { datePattern, timePattern } = getDateTimePatterns({ dateFormat, timeFormat });
  const body = formatInTimeZone(now, timeZone, `EEEE, ${datePattern} '·' ${timePattern}`);
  const longZone = formatInTimeZone(now, timeZone, 'zzzz');
  const shortZone = formatInTimeZone(now, timeZone, 'z');
  const zonePart = resolvedLocale === 'en-US' ? `${longZone} (${shortZone})` : longZone;

  return {
    label: `${body} · ${zonePart}`,
    dateTimeAttr: formatInTimeZone(now, timeZone, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    preferenceLabels: {
      startOfWeek: START_OF_WEEK_OPTIONS.find((o) => o.value === startOfWeek)?.label ?? startOfWeek,
      dateFormat: DATE_FORMATS_OPTIONS.find((o) => o.value === dateFormat)?.label ?? dateFormat,
      timeFormat: TIME_FORMATS_OPTIONS.find((o) => o.value === timeFormat)?.label ?? timeFormat,
    },
  };
}

export function formatSchedule(data, settings, includePrefix = false) {
  const { type, frequency, schedule } = data;
  if (!schedule) return '';

  const { datePattern, timePattern } = getDateTimePatterns(settings);

  // 1. One-Time or Annual (use executionDate)
  if (type === REMINDER_TYPES.ONE_TIME || frequency === REMINDER_FREQUENCIES.ANNUAL) {
    if (!schedule.executionDate) return 'Not set';
    const date = new Date(schedule.executionDate);
    const formatted = format(date, `${datePattern} 'at' ${timePattern}`);

    if (includePrefix) {
      if (type === REMINDER_TYPES.ONE_TIME) return `Once at ${formatted}`;
      if (frequency === REMINDER_FREQUENCIES.ANNUAL) return `Annually on ${formatted}`;
    }
    return formatted;
  }

  // 2. Repeating (Daily, Weekly, Monthly)
  let summaryParts = [];

  if (includePrefix) {
    if (frequency === REMINDER_FREQUENCIES.DAILY) summaryParts.push('Daily');
    if (frequency === REMINDER_FREQUENCIES.WEEKLY) summaryParts.push('Weekly');
    if (frequency === REMINDER_FREQUENCIES.MONTHLY) summaryParts.push('Monthly');
  }

  // Frequency specific part
  if (frequency === REMINDER_FREQUENCIES.WEEKLY && schedule.daysOfWeek?.length) {
    const days = schedule.daysOfWeek.map(d =>
      DAYS_OF_WEEK_SHORT_OPTIONS.find(opt => opt.id === d)?.label || d
    );
    summaryParts.push(`on ${days.join(', ')}`);
  } else if (frequency === REMINDER_FREQUENCIES.MONTHLY && schedule.daysOfMonth?.length) {
    summaryParts.push(`on day ${schedule.daysOfMonth.join(', ')}`);
  }

  // Time part
  if (schedule.time) {
    const [hours, minutes] = schedule.time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    summaryParts.push(`at ${format(date, timePattern)}`);
  }

  return summaryParts.join(' ');
}
